import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  FileBarChart, Download, Trash2, ExternalLink, Search,
  Globe, FileText, Loader2, AlertCircle,
} from "lucide-react";

const PRIORITY_COLOR = {
  Critical: "text-[color:var(--auditiq-critical)] border-[color:var(--auditiq-critical)]/40 bg-[color:var(--auditiq-critical)]/10",
  High:     "text-orange-300 border-orange-400/40 bg-orange-400/10",
  Medium:   "text-gold border-gold/40 bg-gold/10",
  Standard: "text-steel border-steel/30 bg-steel/10",
  Low:      "text-cream-dim border-steel/30 bg-steel/10",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ReportRow = ({ r, onDelete, onDownload }) => {
  const isCrawl = r.report_type === "crawler_scan";
  return (
    <tr className="border-b border-gold-dim/10" data-testid={`report-row-${r.report_id}`}>
      <td className="p-3 text-cream-dim text-xs whitespace-nowrap">{fmt(r.created_at)}</td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          {isCrawl
            ? <Globe size={14} className="text-electric" />
            : <FileText size={14} className="text-gold" />}
          <span className="text-cream text-sm">{r.organization_name || "—"}</span>
          {r.is_demo && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-gold/40 text-gold bg-gold/10">
              Demo
            </span>
          )}
        </div>
        {isCrawl && r.scanned_url && (
          <div className="text-[11px] text-cream-dim mt-1 font-mono truncate max-w-[320px]">{r.scanned_url}</div>
        )}
        {!isCrawl && r.source_file && (
          <div className="text-[11px] text-cream-dim mt-1 font-mono truncate max-w-[320px]">{r.source_file}</div>
        )}
      </td>
      <td className="p-3 text-cream-dim text-xs">{r.created_by || "—"}</td>
      <td className="p-3 text-gold font-mono text-xs">{r.findings_count ?? 0}</td>
      <td className="p-3">
        {r.chet_priority && (
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
            PRIORITY_COLOR[r.chet_priority] || PRIORITY_COLOR.Standard
          }`}>{r.chet_priority}</span>
        )}
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-2">
          <Link
            to={isCrawl
              ? `/auditiq/dashboard?crawl_id=${r.report_id}`
              : `/auditiq/dashboard?audit_id=${r.report_id}`}
            className="text-electric hover:text-gold"
            title="View Dashboard"
            data-testid={`report-view-${r.report_id}`}
          ><ExternalLink size={14} /></Link>
          {r.can_download_pdf && r.download_url && (
            <button
              onClick={() => onDownload(r)}
              className="text-gold hover:text-gold"
              title="Download PDF"
              data-testid={`report-pdf-${r.report_id}`}
            ><Download size={14} /></button>
          )}
          <button
            onClick={() => onDelete(r)}
            className="text-[color:var(--auditiq-critical)] hover:text-gold"
            title="Delete Report"
            data-testid={`report-delete-${r.report_id}`}
          ><Trash2 size={14} /></button>
        </div>
      </td>
    </tr>
  );
};

const Inner = () => {
  const [grouped, setGrouped] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/auditiq/reports/all");
      setGrouped(r.data?.grouped || {});
      setTotal(r.data?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load reports");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.organization_name}" report? This cannot be undone.`)) return;
    try {
      await api.delete(`/auditiq/reports/${r.report_id}?report_type=${r.report_type}`);
      toast.success("Report deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleDownload = async (r) => {
    try {
      const res = await api.get(r.download_url.replace(/^\/api/, ""), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `auditiq-${r.organization_name?.replace(/\s+/g, "-").toLowerCase() || "report"}-${r.report_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error("Download failed");
    }
  };

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return grouped;
    const out = {};
    for (const [module, rows] of Object.entries(grouped)) {
      const kept = rows.filter(r =>
        (r.organization_name || "").toLowerCase().includes(q) ||
        (r.scanned_url || "").toLowerCase().includes(q) ||
        (r.source_file || "").toLowerCase().includes(q) ||
        (r.created_by || "").toLowerCase().includes(q)
      );
      if (kept.length) out[module] = kept;
    }
    return out;
  }, [grouped, query]);

  const moduleKeys = Object.keys(filteredGroups).sort();

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ Reports</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2 text-cream">Your Report History</h1>
          <p className="text-sm text-cream-dim mt-1">
            Every upload audit and crawler scan you've run — grouped by module.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass px-3 py-2 text-xs text-cream-dim">
            <span className="text-gold font-mono text-base">{total}</span> total reports
          </div>
          <Link to="/auditiq/upload">
            <Button className="btn-gold h-10 px-4" data-testid="reports-new-upload-btn">
              <FileText size={14} className="mr-2" /> New Upload Audit
            </Button>
          </Link>
          <Link to="/auditiq/crawler">
            <Button className="btn-ghost-gold h-10 px-4" data-testid="reports-new-crawl-btn">
              <Globe size={14} className="mr-2" /> New Crawler Scan
            </Button>
          </Link>
        </div>
      </div>

      <div className="glass p-4 flex items-center gap-3">
        <Search size={14} className="text-cream-dim" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by organization, URL, file, or user…"
          className="flex-1 bg-navy-deep border-gold-dim/40 text-cream"
          data-testid="reports-search-input"
        />
      </div>

      {loading && (
        <div className="glass p-8 text-center text-cream-dim" data-testid="reports-loading">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Loading reports…
        </div>
      )}

      {error && !loading && (
        <div className="glass p-8 text-center" data-testid="reports-error">
          <AlertCircle size={20} className="inline mr-2 text-[color:var(--auditiq-critical)]" />
          <span className="text-cream-dim">{error}</span>
        </div>
      )}

      {!loading && !error && moduleKeys.length === 0 && (
        <div className="glass p-10 text-center" data-testid="reports-empty">
          <FileBarChart size={28} className="mx-auto text-gold/60 mb-3" />
          <div className="text-cream-dim">
            {query ? "No reports match your search." : "No reports yet. Run an audit or crawler scan to see it here."}
          </div>
        </div>
      )}

      {!loading && !error && moduleKeys.map(module => (
        <div key={module} className="glass p-0 overflow-x-auto" data-testid={`reports-group-${module.toLowerCase().replace(/\s+|\//g, "-")}`}>
          <div className="px-5 py-3 border-b border-gold-dim/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileBarChart size={14} className="text-gold" />
              <h2 className="font-display text-lg text-cream">{module}</h2>
            </div>
            <span className="text-[11px] uppercase tracking-[0.22em] text-cream-dim">
              {filteredGroups[module].length} reports
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
              <tr className="border-b border-gold-dim/20">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Organization</th>
                <th className="text-left p-3">By</th>
                <th className="text-left p-3">Findings</th>
                <th className="text-left p-3">Priority</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups[module].map(r => (
                <ReportRow key={`${r.report_type}-${r.report_id}`} r={r}
                           onDelete={handleDelete} onDownload={handleDownload} />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const AuditIQReports = () => <PortalLayout><Inner /></PortalLayout>;
export default AuditIQReports;
